import { useState, useCallback } from 'react';
import { message } from 'antd';
import { d205Api, type FD150M, type FD151M } from '../../../services/d205Api';
import type { Mode } from './useD205Mode';

const emptyMaster = (): FD150M => ({
  fd150_01: '',
  fd150_10: '',
  fd150_11: '',
  fd150_12: null,
  fd150_13: '',
  fd150_14: '',
  fd150_15: '',
  fd150_16: false,
  fd150_17: false,
  fd150_18: 0,
  fd150_19: 0,
  fd150_20: 0,
  fd150_21: 0,
  fd150_22: 0,
  fd150_23: null,
  fd150_24: null,
  fd150_25: '',
  fd150_26: false,
  fd150_27: false,
  fd150_28: null,
  fd150_80: '',
  fd150_87: '',
  fd150_88: null,
  fd150_89: '',
  fd150_90: 0,
  fd150_91: '',
  fd150_92: null,
  fd150_93: '',
  details: [],
});

const emptyDetail = (seq: number): FD151M => ({
  fd151_01: '',
  fd151_02: seq,
  fd151_10: '',
  fd151_11: '',
  fd151_12: '',
  fd151_13: '',
  fd151_14: '',
  fd151_15: 0,
  fd151_16: 0,
  fd151_17: 0,
  fd151_18: 0,
  fd151_19: 0,
  fd151_20: 0,
  fd151_21: 1,
  fd151_90: 0,
});

export function useD205Record(setMode: (m: Mode) => void) {
  const [record, setRecord] = useState<FD150M | null>(null);
  const [formData, setFormData] = useState<FD150M>(emptyMaster());
  const [details, setDetails] = useState<FD151M[]>([]);
  const [loading, setLoading] = useState(false);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const m = await d205Api.get(id);
      setRecord(m);
      setFormData({ ...m, details: undefined });
      setDetails(m.details ?? []);
    } catch (err) {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const getField = useCallback(
    <K extends keyof FD150M>(key: K): FD150M[K] => formData[key],
    [formData]
  );

  const setField = useCallback(<K extends keyof FD150M>(key: K, value: FD150M[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData(emptyMaster());
    setDetails([]);
    setMode('add');
  }, [setMode]);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record, details: undefined });
      setDetails(record.details ?? []);
    }
    setMode('browse');
  }, [record, setMode]);

  const handleSave = useCallback(
    async (mode: Mode): Promise<boolean> => {
      setLoading(true);
      try {
        const req = { master: { ...formData, details: undefined }, details };
        let saved: FD150M;
        if (mode === 'add') {
          saved = await d205Api.create(req);
        } else {
          saved = await d205Api.update(formData.fd150_01, req);
        }
        setRecord(saved);
        setFormData({ ...saved, details: undefined });
        setDetails(saved.details ?? []);
        setMode('browse');
        message.success('存檔成功');
        return true;
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { errors?: { message: string }[] } } };
        const errs = axiosErr?.response?.data?.errors;
        if (errs?.length) {
          message.error(errs.map((e) => e.message).join('\n'));
        } else {
          message.error('存檔失敗');
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [formData, details, setMode]
  );

  const handleDelete = useCallback(
    async (id: string): Promise<boolean> => {
      setLoading(true);
      try {
        await d205Api.delete(id);
        setRecord(null);
        setFormData(emptyMaster());
        setDetails([]);
        setMode('browse');
        message.success('刪除成功');
        return true;
      } catch {
        message.error('刪除失敗');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [setMode]
  );

  const addDetailRow = useCallback(() => {
    setDetails((prev) => {
      if (prev.length >= 99) return prev;
      return [...prev, emptyDetail(prev.length + 1)];
    });
  }, []);

  const deleteDetailRow = useCallback((idx: number) => {
    setDetails((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      // Re-index
      return next.map((d, i) => ({ ...d, fd151_02: i + 1 }));
    });
  }, []);

  const updateDetailField = useCallback(
    <K extends keyof FD151M>(idx: number, key: K, value: FD151M[K]) => {
      setDetails((prev) =>
        prev.map((d, i) => (i === idx ? { ...d, [key]: value } : d))
      );
    },
    []
  );

  return {
    record,
    formData,
    details,
    loading,
    loadRecord,
    getField,
    setField,
    resetForAdd,
    revertToRecord,
    handleSave,
    handleDelete,
    addDetailRow,
    deleteDetailRow,
    updateDetailField,
  };
}
